import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/Layout";
import { supabase } from "@/integrations/supabase/client";
import { Download } from "lucide-react";
interface Framework {
  id: string;
  title: string;
  description: string;
  cover_image_url?: string;
  file_url?: string;
  slug: string;
}
interface AITool {
  id: string;
  name: string;
  description: string;
  use_case_tags?: string[];
  external_link: string;
  logo_url?: string;
}
const Resources = () => {
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [aiTools, setAITools] = useState<AITool[]>([]);
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [showFrameworks, setShowFrameworks] = useState(true);
  const [showAITools, setShowAITools] = useState(true);
  const [resourcesOrder, setResourcesOrder] = useState<string[]>(["frameworks", "ai_tools"]);
  useEffect(() => {
    loadData();
  }, []);
  const loadData = async () => {
    setLoading(true);
    try {
      const [frameworksData, aiToolsData, settingsData] = await Promise.all([supabase.from("frameworks").select("*").order("created_at", {
        ascending: false
      }), supabase.from("ai_tools").select("*").order("created_at", {
        ascending: false
      }).limit(6), supabase.from("site_settings").select("setting_key, setting_value").in("setting_key", ["show_frameworks", "show_ai_tools", "resources_order"])]);
      if (frameworksData.data) setFrameworks(frameworksData.data);
      if (aiToolsData.data) setAITools(aiToolsData.data);

      // Process settings first
      if (settingsData.data) {
        settingsData.data.forEach(setting => {
          if (setting.setting_key === 'resources_order') {
            try {
              const order = (JSON.parse(setting.setting_value) as string[]).filter(k => k !== "prompts");
              setResourcesOrder(order);
            } catch (e) {
              console.error('Error parsing resources_order:', e);
              setResourcesOrder(["frameworks", "ai_tools"]);
            }
          } else {
            const value = setting.setting_value === 'true';
            switch (setting.setting_key) {
              case 'show_frameworks':
                setShowFrameworks(value);
                break;
              case 'show_ai_tools':
                setShowAITools(value);
                break;
            }
          }
        });
      } else {
        // Set default order if no settings found
        setResourcesOrder(["frameworks", "ai_tools"]);
      }
      setSettingsLoaded(true);
    } catch (error) {
      console.error("Error loading data:", error);
      // Set defaults on error
      setResourcesOrder(["frameworks", "ai_tools"]);
      setSettingsLoaded(true);
    } finally {
      setLoading(false);
    }
  };
  const renderFrameworksSection = () => {
    if (!showFrameworks) return null;
    return <section className="mb-12 md:mb-16" key="frameworks">
        <div className="flex items-center justify-between mb-6 md:mb-8 px-4">
          <h2 className="text-2xl md:text-3xl font-bold">Templates & Frameworks</h2>
          <Button variant="outline" asChild>
            <Link to="/resources/templates-and-frameworks">
              View All
            </Link>
          </Button>
        </div>
        {loading ? <Card className="p-8 text-center mx-4">
            <p className="text-muted-foreground">Loading frameworks...</p>
          </Card> : frameworks.length > 0 ? <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 px-4">
            {frameworks.slice(0, 3).map(framework => <Card key={framework.id} className="hover:shadow-lg transition-shadow">
                {framework.cover_image_url && <div className="aspect-video bg-muted rounded-t-lg overflow-hidden">
                    <img src={framework.cover_image_url} alt={framework.title} className="w-full h-full object-cover" />
                  </div>}
                <CardHeader>
                  <CardTitle className="text-lg">{framework.title}</CardTitle>
                  <CardDescription>{framework.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild className="w-full">
                    <Link to={`/resources/frameworks/${framework.slug}`}>
                      <Download className="w-4 h-4 mr-2" />
                      View Framework
                    </Link>
                  </Button>
                </CardContent>
              </Card>)}
          </div> : <Card className="p-8 text-center mx-4">
            <p className="text-muted-foreground">No frameworks available yet. Check back soon!</p>
          </Card>}
      </section>;
  };
  const renderAIToolsSection = () => {
    if (!showAITools) return null;
    return <section className="mb-12 md:mb-16" key="ai_tools">
        <div className="flex items-center justify-between mb-6 md:mb-8 px-4">
          <h2 className="text-2xl md:text-3xl font-bold">AI Tools Directory</h2>
          <Button variant="outline" asChild>
            <Link to="/resources/ai-tools">
              View All
            </Link>
          </Button>
        </div>
        {loading ? <Card className="p-8 text-center mx-4">
            <p className="text-muted-foreground">Loading AI tools...</p>
          </Card> : aiTools.length > 0 ? <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 px-4">
            {aiTools.slice(0, 3).map(tool => <Card key={tool.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start gap-3 mb-3">
                    {tool.logo_url && <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                        <img src={tool.logo_url} alt={`${tool.name} logo`} className="w-full h-full object-cover" />
                      </div>}
                    <CardTitle className="text-lg">{tool.name}</CardTitle>
                  </div>
                  <CardDescription className="line-clamp-3">{tool.description}</CardDescription>
                  {tool.use_case_tags && tool.use_case_tags.length > 0 && <div className="flex flex-wrap gap-2 mt-3">
                      {tool.use_case_tags.slice(0, 2).map(tag => <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>)}
                    </div>}
                </CardHeader>
                <CardContent>
                  <Button asChild variant="outline" className="w-full">
                    <a href={tool.external_link} target="_blank" rel="noopener noreferrer">
                      Visit Tool
                    </a>
                  </Button>
                </CardContent>
              </Card>)}
          </div> : <Card className="p-8 text-center mx-4">
            <p className="text-muted-foreground">No AI tools available yet. Check back soon!</p>
          </Card>}
      </section>;
  };
  const sectionRenderers: Record<string, () => JSX.Element | null> = {
    frameworks: renderFrameworksSection,
    ai_tools: renderAIToolsSection,
  };
  return <Layout>
      <div className="py-8 md:py-12">
        <div className="container-modern">
          <div className="text-center mb-8 md:mb-12 px-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">Free Resources for Marketers</h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">Practical tools, prompts, and frameworks to help marketers work smarter with AI.</p>
          </div>

          {/* Show loading state until settings are loaded */}
          {!settingsLoaded ? <Card className="p-8 text-center mx-4">
              <p className="text-muted-foreground">Loading resources...</p>
            </Card> : (/* Render sections in the order specified by CMS */
        resourcesOrder.map(sectionKey => {
          const renderer = sectionRenderers[sectionKey];
          return renderer ? renderer() : null;
        }))}
        </div>
      </div>
    </Layout>;
};
export default Resources;
